import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { css } from '@emotion/css';
import { ShadcnButton, ShadcnButtonProps } from '@shadcn-features/core';
import { cx } from 'class-variance-authority';
import { ChevronDown } from 'lucide-react';
import { JSX, ReactNode } from 'react';
import { cn } from '@src/lib/utils';
import ShadcnActionButton from './ShadcnActionButton';

export type ShadcnActionExtras<
    Code extends string = string,
    Extra extends Record<string, unknown> = Record<string, unknown>,
> = Partial<Record<Code, ShadcnActionItemExtra<Extra>>>;

export type ShadcnActionProps<
    Code extends string = string,
    ExtraProps extends Record<string, unknown> = Record<string, unknown>,
> = {
    items?: (ShadcnActionItem<Code> & ExtraProps)[];
    onAction?: (
        action: ShadcnActionItemBase<Code> & ExtraProps,
        extra?: Record<string, unknown>,
    ) => Promise<unknown> | unknown;
    onBeforeAction?: (
        action: ShadcnActionItemBase<Code>,
    ) => Promise<unknown> | unknown;
    actionExtras?: ShadcnActionExtras<Code>;
    gap?: number;
    disabled?: boolean;
    isLoading?: boolean;
    wrapper?:
        | {
              wrapperType?: 'none';
          }
        | {
              wrapperType?: 'float';
          }
        | {
              wrapperType: 'flex';
          }
        | {
              wrapperType: 'popover';
              popoverProps?: {
                  side?: 'top' | 'right' | 'bottom' | 'left';
                  align?: 'start' | 'center' | 'end';
              };
              buttonProps?: ShadcnButtonProps;
              extraActions?: ReactNode[];
              text: ReactNode;
              icon?: ReactNode;
          }
        | {
              wrapperType: 'flowPopover';
              popoverProps?: {
                  side?: 'top' | 'right' | 'bottom' | 'left';
                  align?: 'start' | 'center' | 'end';
              };
              buttonProps?: ShadcnButtonProps;
              extraActions?: ReactNode[];
              text: ReactNode;
              icon?: ReactNode;
          };
};

export type ShadcnActionRenderProps<
    Extra extends Record<string, unknown> = Record<string, unknown>,
> = {
    onBeforeAction?: () => Promise<unknown> | unknown;
    onAction?: (extra?: Extra) => Promise<unknown> | unknown;
    disabled?: boolean;
    triggerText?: string;
} & Omit<ShadcnActionItem, 'render' | 'Component' | 'actionCode'>;

export type ShadcnActionItemExtra<
    Extra extends Record<string, unknown> = Record<string, unknown>,
> = {
    Component?: React.FC<ShadcnActionRenderProps<Extra>>;
    render?: (props: ShadcnActionRenderProps<Extra>) => JSX.Element;
    buttonProps?: Omit<ShadcnButtonProps, 'children' | 'onClick'>;
};

export type ShadcnActionItemBase<Code extends string = string> = {
    actionCode: Code;
    actionText: string;
} & ShadcnActionItemExtra;

export type ShadcnActionItem<
    Code extends string = string,
    Extra extends Record<string, unknown> = Record<string, unknown>,
> = ShadcnActionItemExtra<Extra> & ShadcnActionItemBase<Code>;

const ShadcnAction = <
    Code extends string = string,
    ExtraProps extends Record<string, unknown> = Record<string, unknown>,
>({
    items,
    onAction,
    onBeforeAction,
    actionExtras,
    wrapper,
    disabled,
    gap = 8,
    isLoading,
}: ShadcnActionProps<Code, ExtraProps>) => {
    if (
        (!items || items.length === 0) &&
        (wrapper?.wrapperType === 'popover'
            ? !wrapper?.extraActions || wrapper.extraActions.length === 0
            : false)
    )
        return null;
    if (wrapper?.wrapperType === 'popover') {
        return (
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <ShadcnButton {...wrapper.buttonProps}>
                        {wrapper.icon}
                        {wrapper.text}
                        <ChevronDown className={'size-4'} />
                    </ShadcnButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    align={wrapper.popoverProps?.align || 'end'}
                    className='min-w-0 p-0!'
                    side={wrapper.popoverProps?.side || 'bottom'}
                >
                    {items?.map((action) => (
                        <ShadcnActionButton
                            key={action.actionCode}
                            {...action}
                            Component={ShadcnActionDropdownComponent}
                            actionExtraItem={actionExtras?.[action.actionCode]}
                            disabled={disabled || isLoading}
                            onAction={async (extra) => {
                                await onBeforeAction?.(action);
                                await onAction?.(action, extra);
                            }}
                            onBeforeAction={async () => {
                                await onBeforeAction?.(action);
                            }}
                        />
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
        );
    }

    if (wrapper?.wrapperType === 'flowPopover') {
        const firstItem = items?.[0];
        const otherItems = items?.slice(1);

        const firstItemContent = firstItem && (
            <ShadcnActionButton
                key={firstItem.actionCode}
                {...firstItem}
                actionExtraItem={actionExtras?.[firstItem.actionCode]}
                disabled={disabled || isLoading}
                render={({
                    buttonProps,
                    onBeforeAction,
                    actionText,
                    disabled,
                    onAction,
                }) => {
                    return (
                        <ShadcnButton
                            {...buttonProps}
                            disabled={disabled || buttonProps?.disabled}
                            size={'sm'}
                            variant={'brandGreen'}
                            className={cx(
                                'h-[53px]! w-full text-base! font-medium!',
                                buttonProps?.className,
                            )}
                            onClick={async (_e) => {
                                await onBeforeAction?.();
                                await onAction?.();
                            }}
                        >
                            {actionText}
                        </ShadcnButton>
                    );
                }}
                onAction={async (extra) => {
                    await onBeforeAction?.(firstItem);
                    await onAction?.(firstItem, extra);
                }}
                onBeforeAction={async () => {
                    await onBeforeAction?.(firstItem);
                }}
            />
        );

        return (
            <div className='flex items-center gap-5'>
                {firstItemContent}
                {(otherItems?.length || 0) > 0 && (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <ShadcnButton
                                {...wrapper.buttonProps}
                                variant={'outlinePortal2'}
                                className={cx(
                                    wrapper.buttonProps?.className,
                                    'h-[53px]! w-full text-base! font-medium!',
                                )}
                            >
                                {wrapper.icon}
                                {wrapper.text}
                                <ChevronDown className={'size-4'} />
                            </ShadcnButton>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            align={wrapper.popoverProps?.align || 'end'}
                            className='w-[var(--radix-popper-anchor-width)]! w-full p-0!'
                            side={wrapper.popoverProps?.side || 'bottom'}
                        >
                            {otherItems?.map((action) => (
                                <ShadcnActionButton
                                    key={action.actionCode}
                                    {...action}
                                    disabled={disabled || isLoading}
                                    actionExtraItem={
                                        actionExtras?.[action.actionCode]
                                    }
                                    render={({
                                        buttonProps,
                                        onBeforeAction,
                                        actionText,
                                        disabled,
                                        onAction,
                                    }) => {
                                        return (
                                            <DropdownMenuItem
                                                className={cn('px-4! py-2!')}
                                                disabled={
                                                    disabled ||
                                                    buttonProps?.disabled
                                                }
                                                onClick={async (_e) => {
                                                    await onBeforeAction?.();
                                                    await onAction?.();
                                                }}
                                            >
                                                <ShadcnButton
                                                    {...buttonProps}
                                                    variant={'ghost'}
                                                    className={cx(
                                                        'm-0! p-0! py-0!',
                                                        buttonProps?.className,
                                                        'hover:bg-transparent!',
                                                    )}
                                                >
                                                    {actionText}
                                                </ShadcnButton>
                                            </DropdownMenuItem>
                                        );
                                    }}
                                    onAction={async (extra) => {
                                        await onBeforeAction?.(action);
                                        await onAction?.(action, extra);
                                    }}
                                    onBeforeAction={async () => {
                                        await onBeforeAction?.(action);
                                    }}
                                />
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}
            </div>
        );
    }

    if (wrapper?.wrapperType === 'flex') {
        return (
            <div
                className={css`
                    display: flex;
                    gap: ${gap}px;
                    flex-wrap: wrap;
                `}
            >
                {items?.map((action) => (
                    <ShadcnActionButton
                        key={action.actionCode}
                        {...action}
                        actionExtraItem={actionExtras?.[action.actionCode]}
                        disabled={disabled || isLoading}
                        onAction={async (extra) => {
                            await onBeforeAction?.(action);
                            await onAction?.(action, extra);
                        }}
                        onBeforeAction={async () => {
                            await onBeforeAction?.(action);
                        }}
                    />
                ))}
            </div>
        );
    }

    if (wrapper?.wrapperType === 'float') {
        return (
            <div
                className={css`
                    position: fixed;
                    bottom: 2rem;
                    right: 2rem;
                    display: flex;
                    flex-direction: column;
                    gap: ${gap}px;
                `}
            >
                {items?.map((action) => (
                    <ShadcnActionButton
                        key={action.actionCode}
                        {...action}
                        actionExtraItem={actionExtras?.[action.actionCode]}
                        disabled={disabled || isLoading}
                        onAction={async (extra) => {
                            await onBeforeAction?.(action);
                            await onAction?.(action, extra);
                        }}
                        onBeforeAction={async () => {
                            await onBeforeAction?.(action);
                        }}
                    />
                ))}
            </div>
        );
    }

    return (
        <>
            {items?.map((action) => (
                <ShadcnActionButton
                    key={action.actionCode}
                    {...action}
                    actionExtraItem={actionExtras?.[action.actionCode]}
                    disabled={disabled || isLoading}
                    onAction={async (extra) => {
                        await onBeforeAction?.(action);
                        await onAction?.(action, extra);
                    }}
                    onBeforeAction={async () => {
                        await onBeforeAction?.(action);
                    }}
                />
            ))}
        </>
    );
};

export default ShadcnAction;

export function ShadcnActionDropdownComponent<
    Extra extends Record<string, unknown> = Record<string, unknown>,
>({
    onAction,
    onBeforeAction,
    disabled,
    actionText,
    buttonProps,
    triggerText,
}: ShadcnActionRenderProps<Extra>) {
    return (
        <DropdownMenuItem
            className={cn('px-4! py-1!')}
            disabled={disabled || buttonProps?.disabled}
            onClick={async (e) => {
                await onBeforeAction?.();
                await onAction?.();
            }}
        >
            <ShadcnButton
                {...buttonProps}
                className={cx('m-0! p-0! py-0!', buttonProps?.className)}
                variant={'ghost'}
            >
                {triggerText ?? actionText}
            </ShadcnButton>
        </DropdownMenuItem>
    );
}
